/**
 * Navigation reference for BunkerOS.
 *
 * Single source of truth mapping every menu page to its **page id** (the
 * Design-Studio page GUID), its **slug** (the route path), and its display
 * **name** + icon. Derived from the design layout's `menuData` variable and the
 * project's PageDesignMap. The app routes by SLUG, never by page id — this file
 * is the id ↔ slug ↔ name lookup.
 *
 * Keep in sync as pages are generated: `ui-components-from-reference` adds/updates
 * entries and points routes at the real component (until then the slug falls
 * through to the 404 catch-all).
 *
 * Fields:
 *   - pageId:       Design-Studio page GUID
 *   - slug:         route path segment (leading slash added by the router)
 *   - label:        display name in the navigation (kept to ONE word so the
 *                   dense primary nav never overflows the bar)
 *   - icon:         Font Awesome class
 *   - inNav:        show in the primary top navigation
 *   - authRequired: route is gated behind an authenticated session
 *   - roles:        business roles that see this item in the nav (RBAC). Omit or
 *                   leave empty for items every authenticated user can see.
 *                   Administrator always sees every nav item (see navItemsForRole).
 */

/** Business role names (must match backend/seeds/01_users.js `role` values). */
export const ROLES = {
  OPS: 'Operations Coordinator',
  DISPATCHER: 'Dispatcher',
  CREDIT: 'Credit Controller',
  CLAIMS: 'Claims Analyst',
  FINANCE: 'Finance Officer',
  BARGE: 'Barge Master',
  ADMIN: 'Administrator',
  BUYER: 'Buyer',
};

// External (customer) roles are walled off from the internal operations shell:
// they see ONLY the nav items that explicitly name them, never the shared
// internal home. Internal staff see shared items plus their role's items.
const EXTERNAL_ROLES = new Set([ROLES.BUYER]);

export const MENU_ITEMS = [
  {
    pageId: '2461dad7-93d8-4a79-8874-e15fcaf49c9b',
    slug: 'operations-console',
    label: 'Operations',
    icon: 'fas fa-chart-line',
    inNav: true,
    authRequired: true,
    isDefault: true,
    roles: [], // shared home for every role
  },
  {
    pageId: 'a268bf73-e867-4730-93cc-87f652982e2a',
    slug: 'credit-compliance-board',
    label: 'Credit',
    icon: 'fas fa-wallet',
    inNav: true,
    authRequired: true,
    roles: [ROLES.CREDIT],
  },
  {
    pageId: '57cf969d-3126-4456-848e-6e85c50f97f1',
    slug: 'nomination-intake-console',
    label: 'Nominations',
    icon: 'fas fa-file-import',
    inNav: true,
    authRequired: true,
    roles: [ROLES.OPS],
  },
  {
    pageId: 'f3d25012-c450-412c-8e9b-e752eac8549c',
    slug: 'scheduling-gantt-board',
    label: 'Scheduling',
    icon: 'fas fa-calendar-alt',
    inNav: true,
    authRequired: true,
    roles: [ROLES.DISPATCHER],
  },
  {
    pageId: '8a35c6cf-28ab-48c0-9e5a-33aa5ccfdd85',
    slug: 'invoicing-erp-console',
    label: 'Invoicing',
    icon: 'fas fa-file-invoice-dollar',
    inNav: true,
    authRequired: true,
    roles: [ROLES.FINANCE],
  },
  {
    pageId: '478d582b-0fe9-4ae9-a005-e4a988c7e113',
    slug: 'barge-master-execution-screen',
    label: 'Execution',
    icon: 'fas fa-ship',
    inNav: true,
    authRequired: true,
    roles: [ROLES.BARGE],
  },
  {
    pageId: '8ed9c696-3219-4e58-855c-76972bf6964f',
    slug: 'computations-quality-auditor',
    label: 'Quality',
    icon: 'fas fa-flask',
    inNav: true,
    authRequired: true,
    roles: [ROLES.OPS, ROLES.CLAIMS],
  },
  {
    // NOTE: the real route/component is 'rules-packs-administration' (see
    // App.jsx + pages/rules-packs-administration/). This slug previously read
    // 'rules-pack-configurator', which has no route — the "Rules" nav link
    // 404'd for every Administrator. Fixed.
    pageId: '558bb6f8-ae96-42f6-a92f-ac5ac1c8a5c5',
    slug: 'rules-packs-administration',
    label: 'Rules',
    icon: 'fas fa-cogs',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },
  {
    pageId: '72cf53e7-8e9a-4eab-a6eb-05e384612686',
    slug: 'claims-disputes-manager',
    label: 'Claims',
    icon: 'fas fa-gavel',
    inNav: true,
    authRequired: true,
    roles: [ROLES.OPS, ROLES.CLAIMS],
  },

  // ---- Admin / settings surfaces --------------------------------------
  // These pages existed as routes (App.jsx) but were never added to the nav —
  // Administrator had no way to reach them from the sidebar. Backend role
  // guards (see each module's *.routes.js) confirm these are Administrator
  // surfaces (editing is Admin-only everywhere; a few also allow broader
  // *viewing* server-side, but a dedicated settings nav entry is Admin-only —
  // other roles interact with this data inline within their own consoles).
  {
    pageId: 'c1a1e001-3b3f-4b9e-8f2a-1a2b3c4d5e01',
    slug: 'organization-entities-settings',
    label: 'Entities',
    icon: 'fas fa-building',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },
  {
    pageId: 'c1a1e001-3b3f-4b9e-8f2a-1a2b3c4d5e02',
    slug: 'fleet-assets-settings',
    label: 'Fleet',
    icon: 'fas fa-warehouse',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },
  {
    pageId: 'c1a1e001-3b3f-4b9e-8f2a-1a2b3c4d5e03',
    slug: 'integrations-settings',
    label: 'Integrations',
    icon: 'fas fa-plug',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },
  {
    pageId: 'c1a1e001-3b3f-4b9e-8f2a-1a2b3c4d5e04',
    slug: 'operational-parameters-registry',
    label: 'Parameters',
    icon: 'fas fa-sliders',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },
  {
    pageId: 'c1a1e001-3b3f-4b9e-8f2a-1a2b3c4d5e05',
    slug: 'notifications-templates-settings',
    label: 'Notify',
    icon: 'fas fa-bell',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },
  {
    pageId: 'c1a1e001-3b3f-4b9e-8f2a-1a2b3c4d5e06',
    slug: 'buyer-portal-administration',
    label: 'Buyers',
    icon: 'fas fa-handshake',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },
  {
    pageId: 'c1a1e001-3b3f-4b9e-8f2a-1a2b3c4d5e07',
    slug: 'users-roles-approvals-settings',
    label: 'Users',
    icon: 'fas fa-users-gear',
    inNav: true,
    authRequired: true,
    roles: [ROLES.ADMIN],
  },

  {
    // Customer-facing buyer portal (external counterparties). Distinct from the
    // admin-side 'buyer-portal-administration' surface above. Scoped to
    // EXTERNAL_ROLES — see navItemsForRole, which excludes external-only items
    // from the "Administrator sees everything" bypass so staff never get a
    // dead nav link into a route only a Buyer session can pass RoleRoute for.
    pageId: 'b7d1f0a2-6c4e-4a1b-9f3d-2e8c5a90b111',
    slug: 'buyer-portal',
    label: 'Portal',
    icon: 'fas fa-clipboard-list',
    inNav: true,
    authRequired: true,
    roles: [ROLES.BUYER],
  },
  {
    // The login page is reached via the account controls, not the primary nav.
    pageId: '8617fa7a-a0b7-4376-bbf2-5967340e93b5',
    slug: 'login',
    label: 'Login Page',
    icon: 'fas fa-sign-in-alt',
    inNav: false,
    authRequired: false,
  },
];

/** The landing page (design's DefaultMenuItem / is_default_page). */
export const DEFAULT_MENU_ITEM = MENU_ITEMS.find((m) => m.isDefault) || MENU_ITEMS[0];

/**
 * Slug a role should land on after login. External customers (Buyer) land on
 * their portal; everyone else lands on the shared internal default.
 */
export const landingSlugForRole = (role) =>
  role === ROLES.BUYER ? 'buyer-portal' : DEFAULT_MENU_ITEM.slug;

/** Items shown in the primary navigation (all roles, unfiltered). */
export const NAV_ITEMS = MENU_ITEMS.filter((m) => m.inNav);

/**
 * RBAC nav filter: the nav items a given business role should see.
 * - Administrator sees every nav item.
 * - Items with an empty/absent `roles` list are shared (visible to all).
 * - Otherwise the item shows only when the role is in its `roles` list.
 * An unknown/absent role still gets the shared items (e.g. Operations).
 */
export const navItemsForRole = (role) => {
  // External roles (customers) are scoped to items that name them explicitly.
  if (EXTERNAL_ROLES.has(role)) {
    return NAV_ITEMS.filter((m) => m.roles && m.roles.includes(role));
  }
  // Internal roles (incl. Administrator) never see items scoped to an external
  // role — that route's RoleRoute would just bounce them straight back out,
  // so it would render as a dead link in the sidebar.
  const isExternalOnly = (m) => m.roles?.length && m.roles.every((r) => EXTERNAL_ROLES.has(r));
  return NAV_ITEMS.filter((m) => {
    if (isExternalOnly(m)) return false;
    return role === ROLES.ADMIN || !m.roles || m.roles.length === 0 || m.roles.includes(role);
  });
};

/** Look up a menu entry by slug. */
export const findMenuBySlug = (slug) => MENU_ITEMS.find((m) => m.slug === slug);

export default MENU_ITEMS;
